import type {
  FilterClause,
  FilterConfig,
  FilterOp,
  FilterParameter,
} from "../../store/api/dataSourcesApi";

export const FILTER_OPS: { value: FilterOp; label: string }[] = [
  { value: "eq", label: "equals" },
  { value: "neq", label: "not equals" },
  { value: "in", label: "in" },
  { value: "not_in", label: "not in" },
  { value: "gt", label: "greater than" },
  { value: "gte", label: "greater or equal" },
  { value: "lt", label: "less than" },
  { value: "lte", label: "less or equal" },
];

export const BUILTIN_FILTER_SOURCES: { value: string; label: string }[] = [
  { value: "consumer_org_id", label: "Consumer organisation" },
  { value: "consumer_user_id", label: "Consumer user" },
  { value: "org_id", label: "Lumina organisation" },
  { value: "user_id", label: "Caller user" },
  { value: "application_id", label: "Application" },
  { value: "custom", label: "Custom request parameter" },
];

export function emptyFilterConfig(): FilterConfig {
  return { parameters: [], clauses: [] };
}

export function isEmptyFilterConfig(config: FilterConfig | null | undefined): boolean {
  if (!config) return true;
  const params = config.parameters ?? [];
  const clauses = config.clauses ?? [];
  return params.length === 0 && clauses.length === 0;
}

export function clauseValueFrom(clause: FilterClause): string {
  return (clause.valueFrom || clause.value_from || "").trim();
}

/** Payload shape expected by portal create/update (snake_case). */
export function toFilterConfigPayload(
  config: FilterConfig | null | undefined,
): FilterConfig | undefined {
  if (!config || isEmptyFilterConfig(config)) return undefined;
  return {
    parameters: (config.parameters ?? []).map((p) => ({
      name: p.name.trim(),
      source: p.source.trim(),
      required: Boolean(p.required),
      label: p.label?.trim() || p.name,
      description: p.description ?? null,
    })),
    clauses: (config.clauses ?? []).map((c) => {
      const from = clauseValueFrom(c);
      const out: FilterClause = {
        field: (c.field || "").trim(),
        op: c.op || "eq",
      };
      if (from) {
        out.value_from = from;
      } else if (c.op === "in" || c.op === "not_in") {
        const raw = c.value;
        out.value = Array.isArray(raw)
          ? raw
          : String(raw ?? "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
      } else {
        out.value = c.value;
      }
      return out;
    }),
  };
}

export function sourceSelectValue(source: string): string {
  if (!source) return "consumer_org_id";
  if (source.startsWith("parameters.")) return "custom";
  return source;
}

export function customParamName(source: string): string {
  return source.startsWith("parameters.") ? source.slice("parameters.".length) : "";
}

export function defaultParameter(): FilterParameter {
  return {
    name: "consumer_org_id",
    source: "consumer_org_id",
    required: true,
    label: "Consumer organisation",
  };
}

export function defaultClause(paramName?: string): FilterClause {
  if (paramName) {
    return { field: "", op: "eq", value_from: paramName };
  }
  return { field: "visibility", op: "eq", value: "public" };
}
